import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { CheckRounded, ErrorOutlineOutlined, HourglassEmptyRounded, ListAltOutlined, RemoveRounded } from "@mui/icons-material";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { lwfEmployeeHeader } from "pages/PayRoll/PayRollHeaders";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList, StatutoryPage, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { fetchLwfSites } from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/lwfConfigApi";
import { fetchLwfEmployee, fetchLwfEmployees } from "./lwfEmployeesApi";
import { num } from "./lwfEmployeesData";
import { CONFIG_ROUTE, LwfEmployeePanel } from "./LwfEmployeePanel";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Statutory > Labour Welfare Fund > Employees (screens LWF2–
// LWF2b): each employee's LWF site, category, coverage and next deduction,
// worked out by the server from the site's rules (GET employees/). Nothing
// is typed here. Search, cards and paging go to the server; amounts per
// payroll month are in Reports.

const ALL = "all";
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200;
const SEARCH_DELAY = 400;

export const StatutoryLWF = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <LwfEmployeesScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

const LwfEmployeesScreen = ({ domain, companyId }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const [sites, setSites] = useState([]);
  const [card, setCard] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0, cards: {} });
  const [viewId, setViewId] = useState(null);
  const [details, setDetails] = useState({ loading: false, error: "", detail: null });
  const [detailsKey, setDetailsKey] = useState(0);
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "lwf_employees_saved_views",
    fullHeaders: lwfEmployeeHeader,
  });

  // Sites for the filter and the "site not set up" banner.
  useEffect(() => {
    if (!companyId) return undefined;
    const controller = new AbortController();
    fetchLwfSites({ domain, companyId, signal: controller.signal })
      .then((data) => setSites(data.rows))
      .catch(() => {});
    return () => controller.abort();
  }, [domain, companyId]);

  // Search waits for typing to pause, then starts again from page 1.
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!companyId) return undefined;
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchLwfEmployees({ domain, companyId, card, search: query, page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList((current) => ({ ...current, loading: false, error: error.message || "Couldn't load the LWF employees." }));
      });
    return () => controller.abort();
  }, [domain, companyId, card, query, page, pageSize, reloadKey]);

  useEffect(() => {
    if (viewId == null) {
      setDetails({ loading: false, error: "", detail: null });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ loading: true, error: "", detail: current.detail?.row.id === viewId ? current.detail : null }));
    fetchLwfEmployee({ domain, companyId, employeeId: viewId, signal: controller.signal })
      .then((detail) => setDetails({ loading: false, error: "", detail }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ loading: false, error: error.message || "Couldn't load the employee's LWF details.", detail: null });
      });
    return () => controller.abort();
  }, [domain, companyId, viewId, detailsKey]);

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  // Export: every matching row, page by page.
  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchLwfEmployees({ domain, companyId, card, search: query, page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...data.rows);
      if (!data.rows.length || all.length >= data.count) return all;
    }
  }, [domain, companyId, card, query]);

  const cardValue = (key) => num(list.cards?.[key]);
  const cards = [
    { key: ALL, title: "All", value: cardValue("all"), icon: ListAltOutlined, tone: "blue" },
    { key: "covered", title: "Covered", value: cardValue("covered"), icon: CheckRounded, tone: "green" },
    { key: "not_covered", title: "Not covered", value: cardValue("not_covered"), icon: RemoveRounded, tone: "amber" },
    { key: "held", title: "Held", value: cardValue("held"), icon: HourglassEmptyRounded, tone: "red" },
    { key: "notsetup", title: "Site not set up", value: cardValue("site_not_set_up"), icon: ErrorOutlineOutlined, tone: "red" },
  ];
  // Sites with staff but no LWF set-up (the LWF sites list only has sites
  // with employees or already set up).
  const notSetUpSites = sites.filter((site) => !site.set_up).map((site) => site.site);

  const amount = (value) => (value == null ? "—" : num(value));
  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "lwf_wage":
        return amount(row.lwf_wage);
      case "status_label":
        return <ToneChip label={row.status_label} tone={row.status_tone} />;
      case "employee_amount":
        return amount(row.employee_amount);
      case "employer_amount":
        return amount(row.employer_amount);
      default:
        return row[key] || "—";
    }
  };

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Statutory", "Labour Welfare Fund", "Employees"]}
      title="LWF Employees"
      switchAction={<OpenSettingsButton group="statutory" item="lwf" />}
      subtitle="Who pays LWF at each site and why someone is not covered. Worked out from the site's rules — nothing to type here. Amounts are in Reports › Statutory Reports › LWF."
    >
      <StatCardGrid cards={cards} value={card} onChange={resetPage(setCard)} />

      <Mui.Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {list.error ? (
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the LWF employees."
            body={list.error}
            action={
              <SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>
                Retry
              </SecondaryButton>
            }
          />
        ) : null}
        {notSetUpSites.length ? (
          <RequestInfoBanner
            tone="danger"
            body={`${notSetUpSites.join(", ")} ${notSetUpSites.length === 1 ? "has" : "have"} staff but no LWF set-up. Set it up, or mark it Not applicable, in Configuration › Statutory › Labour Welfare Fund.`}
            action={
              <SecondaryButton onClick={() => history?.push(CONFIG_ROUTE)} sx={{ whiteSpace: "nowrap" }}>
                Set up site
              </SecondaryButton>
            }
          />
        ) : null}
      </Mui.Stack>

      <StatutoryList
        title="LWF Employees"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name or ID"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
        }}
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Coverage follows the site's “Who pays” rules: wage limit, categories not covered, and being on the rolls on the site's set date. Missing data is never guessed: LWF is held until it is fixed. To change it, change the employee's category or the site's rules." />
      </Mui.Box>

      <LwfEmployeePanel
        open={viewId != null}
        loading={details.loading && !details.detail}
        error={details.error}
        detail={details.detail}
        onRetry={() => setDetailsKey((key) => key + 1)}
        onClose={() => setViewId(null)}
      />
    </StatutoryPage>
  );
};
